/**
 * NumericPad — phone-dialer 3×4 ввод для рабочего PWA.
 *
 * Используется в трёх местах (см. §3.2 в docs/tasks/17_worker-time-tracking.md):
 *  - PIN-pad внутри `<PinSheet>` (length=4, masked dots снаружи)
 *  - tab_number ввод на `/login` worker-flow (length={min:3,max:6})
 *  - может быть переиспользован под произвольный numeric input
 *
 * Дизайн:
 *  - НЕ обёртка над `<input type="text">` — собственный pad. Это убирает
 *    глючные мобильные клавиатуры (auto-suggest, T9, паста цифр с пробелами),
 *    но цена — нужно самому ловить hardware-keyboard через `onKeyDown`
 *    обёрнутого контейнера (для accessibility и desktop-fallback).
 *  - Плитки рендерятся через CSS-переменные `--tile-size` / `--tile-radius`
 *    / `--tile-font` (см. tokens.css). Единый knob с HoursStepper (§3.5).
 *  - Visual idiom — индустриальная «штампованная» кнопка-плунжер:
 *    белый фон, rule-line обводка, hard-offset 1px shadow, `:active` —
 *    bg-panel-2 + inset shadow («нажалась внутрь»). Никаких scale/bounce.
 */
import * as React from "react";
import { Delete, Check } from "lucide-react";
import { cn } from "@/lib/utils";

export type NumericPadLength = number | { min: number; max: number };

export interface NumericPadProps {
  /** Фиксированная длина (e.g. PIN=4) или диапазон (tab_number {3,6}). */
  length: NumericPadLength;
  /** Текущее значение (только цифры). */
  value: string;
  /** Колбэк при изменении (после digit/backspace). */
  onChange: (next: string) => void;
  /**
   * Колбэк сабмита: вызывается при тапе ✓ ИЛИ автоматически при достижении
   * max длины (только если length — число, не диапазон). Для диапазона
   * сабмит ТОЛЬКО ручной ✓.
   */
  onSubmit: (value: string) => void;
  /** Блокировка ввода (например на время submitting). */
  disabled?: boolean;
  /** Aria-label на контейнер (по умолчанию — generic «Цифровая клавиатура»). */
  "aria-label"?: string;
  id?: string;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
  "aria-describedby"?: string;
  /** data-testid на корневой контейнер. */
  "data-testid"?: string;
}

function normaliseLength(length: NumericPadLength): { min: number; max: number; fixed: boolean } {
  if (typeof length === "number") return { min: length, max: length, fixed: true };
  return { min: length.min, max: length.max, fixed: false };
}

export function NumericPad({
  length,
  value,
  onChange,
  onSubmit,
  disabled = false,
  "aria-label": ariaLabel = "Цифровая клавиатура",
  id,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  "data-testid": testId,
}: NumericPadProps): React.ReactElement {
  const { min, max, fixed } = normaliseLength(length);

  const canSubmit = !disabled && value.length >= min;
  const isFull = value.length >= max;

  const pressDigit = React.useCallback(
    (digit: string) => {
      if (disabled || isFull) return;
      const next = value + digit;
      onChange(next);
      // Auto-submit ТОЛЬКО для фиксированной длины — для диапазона ждём ✓.
      if (fixed && next.length === max) {
        onSubmit(next);
      }
    },
    [disabled, isFull, value, onChange, onSubmit, fixed, max],
  );

  const pressBackspace = React.useCallback(() => {
    if (disabled || value.length === 0) return;
    onChange(value.slice(0, -1));
  }, [disabled, value, onChange]);

  const pressSubmit = React.useCallback(() => {
    if (!canSubmit) return;
    onSubmit(value);
  }, [canSubmit, onSubmit, value]);

  // Hardware-keyboard поддержка — desktop browser, BT-клавиатура на планшете.
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;
      if (event.key >= "0" && event.key <= "9") {
        event.preventDefault();
        pressDigit(event.key);
      } else if (event.key === "Backspace") {
        event.preventDefault();
        pressBackspace();
      } else if (event.key === "Enter") {
        event.preventDefault();
        pressSubmit();
      }
    },
    [disabled, pressDigit, pressBackspace, pressSubmit],
  );

  return (
    <div
      id={id}
      role="group"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={handleKeyDown}
      data-testid={testId}
      className="numeric-pad inline-grid grid-cols-3 outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
      style={{
        gap: "var(--tile-gap)",
        // focus ring через accent-agent (heat-bright) — fits factory theme
        ["--tw-ring-color" as string]: "var(--accent-agent)",
      }}
    >
      {DIGITS.map((digit) => (
        <NumericTile
          key={digit}
          label={digit}
          onPress={() => pressDigit(digit)}
          disabled={disabled || isFull}
          testId={testId ? `${testId}-digit-${digit}` : undefined}
        />
      ))}
      <NumericTile
        label=""
        ariaLabel="Стереть"
        onPress={pressBackspace}
        disabled={disabled || value.length === 0}
        testId={testId ? `${testId}-backspace` : undefined}
        variant="muted"
      >
        <Delete className="h-6 w-6" aria-hidden />
      </NumericTile>
      <NumericTile
        label="0"
        onPress={() => pressDigit("0")}
        disabled={disabled || isFull}
        testId={testId ? `${testId}-digit-0` : undefined}
      />
      <NumericTile
        label=""
        ariaLabel="Подтвердить"
        onPress={pressSubmit}
        disabled={!canSubmit}
        testId={testId ? `${testId}-submit` : undefined}
        variant="primary"
      >
        <Check className="h-6 w-6" aria-hidden />
      </NumericTile>
    </div>
  );
}

const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"] as const;

interface NumericTileProps {
  label: string;
  ariaLabel?: string;
  onPress: () => void;
  disabled?: boolean;
  testId?: string;
  variant?: "default" | "muted" | "primary";
  children?: React.ReactNode;
}

function NumericTile({
  label,
  ariaLabel,
  onPress,
  disabled = false,
  testId,
  variant = "default",
  children,
}: NumericTileProps): React.ReactElement {
  // Tile chrome:
  //   default — белый плунжер, rule-line обводка, hard-offset shadow
  //   muted   — backspace, нет shadow, чуть тоньше визуально
  //   primary — submit ✓, heat-bright fill когда enabled (live/active)
  return (
    <button
      type="button"
      onClick={onPress}
      disabled={disabled}
      aria-label={ariaLabel ?? label}
      data-testid={testId}
      data-variant={variant}
      className={cn(
        "numeric-tile relative flex items-center justify-center select-none",
        "font-mono font-medium",
        "transition-[background-color,box-shadow,transform] duration-75",
        "disabled:cursor-not-allowed disabled:opacity-40",
        "active:translate-y-[1px]",
      )}
      style={{
        width: "var(--tile-w, var(--tile-size, 64px))",
        height: "var(--tile-h, var(--tile-size, 64px))",
        borderRadius: "var(--tile-radius)",
        fontSize: "var(--tile-font)",
        fontFeatureSettings: '"tnum" 1, "lnum" 1',
        ...getTileVariantStyle(variant),
      }}
    >
      {children ?? label}
    </button>
  );
}

function getTileVariantStyle(variant: "default" | "muted" | "primary"): React.CSSProperties {
  if (variant === "primary") {
    return {
      background: "var(--accent-agent)",
      color: "#ffffff",
      border: "1px solid var(--heat-ember)",
      boxShadow: "1px 1px 0 var(--soot-900)",
    };
  }
  if (variant === "muted") {
    return {
      background: "var(--bg-panel-1)",
      color: "var(--fg-2)",
      border: "1px solid var(--rule-line)",
    };
  }
  return {
    background: "var(--bg-panel-0)",
    color: "var(--fg-1)",
    border: "1px solid var(--rule-line)",
    boxShadow: "var(--shadow-plate)",
  };
}
